import { Stack } from '@mui/material';
import { isCPFOrCNPJ } from 'brazilian-values';
import { useFormik, FormikProvider } from 'formik';
import parsePhoneNumberFromString, {
  parsePhoneNumber,
} from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import { PhoneInputFormik, TaxIDInputFormik, TextField } from '@woovi/form';
import { useMutationCallbacks } from '@woovi/relay';
import { ActionButton } from '@woovi/ui';
import { registerYupValidations } from '@woovi/utils';

import type { PaymentMethodCreditCardCustomerRegisterStep_charge$key } from './__generated__/PaymentMethodCreditCardCustomerRegisterStep_charge.graphql.ts';
import type { ChargeCustomerRegisterMutation } from '../../../customerRegister/mutations/__generated__/ChargeCustomerRegisterMutation.graphql.ts';
import { ChargeCustomerRegister } from '../../../customerRegister/mutations/ChargeCustomerRegisterMutation.ts';

registerYupValidations();

type PaymentMethodCreditCardCustomerRegisterStepProps = {
  charge: PaymentMethodCreditCardCustomerRegisterStep_charge$key;
};

type PaymentMethodCreditCardCustomerRegisterStepFormValues = {
  name: string;
  taxID: string;
  email: string;
  phone: string;
};

const PaymentMethodCreditCardCustomerRegisterStep = (
  props: PaymentMethodCreditCardCustomerRegisterStepProps,
) => {
  const { t } = useTranslation();

  const charge =
    useFragment<PaymentMethodCreditCardCustomerRegisterStep_charge$key>(
      graphql`
        fragment PaymentMethodCreditCardCustomerRegisterStep_charge on Charge {
          id
        }
      `,
      props.charge,
    );

  const [chargeCustomerRegister, isPending] =
    useMutationCallbacks<ChargeCustomerRegisterMutation>({
      name: 'ChargeCustomerRegister',
      mutation: ChargeCustomerRegister,
    });

  const getPhone = (phone: string): { phone?: string } => {
    const phoneWithoutPrefix =
      parsePhoneNumberFromString(phone)?.formatNational();

    if (!phoneWithoutPrefix) {
      return {};
    }

    return { phone: parsePhoneNumber(phone, 'BR').number };
  };

  const formikbag =
    useFormik<PaymentMethodCreditCardCustomerRegisterStepFormValues>({
      initialValues: {
        name: '',
        taxID: '',
        email: '',
        phone: '',
      },
      validationSchema: yup.object().shape({
        name: yup.string().required(t('Name is required')),
        taxID: yup
          .string()
          .test('isCPF', t('Customer TaxID is invalid'), (value) => {
            if (!value) {
              return true;
            }

            return isCPFOrCNPJ(value);
          })
          .required(t('TaxID is required')),
        email: yup
          .string()
          .email(t('Email must be a valid email'))
          .required(t('Email is required')),
        phone: yup
          .string()
          .required(t('Phone is required'))
          .test(
            'phone',
            t('Phone is invalid'),
            (value) => !!(value && parsePhoneNumber(value, 'BR')?.isValid()),
          ),
      }),
      validateOnMount: true,
      onSubmit: (values) => {
        const { name, email, phone, taxID } = values;

        chargeCustomerRegister({
          variables: {
            input: {
              chargeId: charge.id,
              name,
              email,
              ...getPhone(phone),
              taxID,
            },
          },
        });
      },
    });

  const { isValid, isSubmitting, handleSubmit } = formikbag;

  const disabled = !isValid || isSubmitting || isPending;
  const loading = isSubmitting || isPending;

  return (
    <FormikProvider value={formikbag}>
      <Stack sx={{ gap: 1.5 }}>
        <Stack sx={{ gap: 2 }}>
          <TextField
            name='name'
            label={t('Name')}
            placeholder={t('Name')}
            sx={{ m: 0 }}
          />
          <TaxIDInputFormik
            name='taxID'
            label={t('CPF or CNPJ')}
            placeholder={t('CPF or CNPJ')}
            sx={{ m: 0 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              name='email'
              label={t('Email')}
              placeholder={t('Email')}
              sx={{ m: 0 }}
              fullWidth
            />
            <PhoneInputFormik
              name='phone'
              label={t('Phone')}
              type='tel'
              defaultCountry='br'
              onlyCountries={['br']}
              sx={{ m: 0 }}
              fullWidth
            />
          </Stack>
        </Stack>
        <ActionButton
          type='submit'
          disabled={disabled}
          loading={loading}
          onClick={handleSubmit}
          sx={(theme) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
            '&:focus': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
          })}
        >
          {t('Next step')}
        </ActionButton>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentMethodCreditCardCustomerRegisterStep;
